import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGetData,
  type GetOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGetErrors,
  type GetOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGetResponse,
  getOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGet,
} from '../../generated';

export type GetOriginalPdpSnapshotQueryVariables = Omit<
  GetOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGetData['path'],
  'portfolio_id'
> & {
  workspace_id: string;
  organization_id: string;
};

export type GetOriginalPdpSnapshotQuery = GetOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGetResponse;

export const useGetOriginalPdpSnapshotQuery = <
  TData = GetOriginalPdpSnapshotQuery,
  TError = GetOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGetErrors[keyof GetOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGetErrors],
>(
  { organization_id, workspace_id, pdp_id }: GetOriginalPdpSnapshotQueryVariables,
  options?: Omit<UseQueryOptions<GetOriginalPdpSnapshotQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetOriginalPdpSnapshotQuery, TError, TData>({
    queryKey: ['GetOriginalPdpSnapshot', { organization_id, workspace_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGet({
        path: { workspace_id, pdp_id },
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useGetOriginalPdpSnapshotQuery.getKey = ({
  organization_id,
  workspace_id,
  pdp_id,
}: GetOriginalPdpSnapshotQueryVariables) => ['GetOriginalPdpSnapshot', { organization_id, workspace_id, pdp_id }];

useGetOriginalPdpSnapshotQuery.fetcher = (
  { workspace_id, pdp_id }: GetOriginalPdpSnapshotQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getOriginalPdpSnapshotV1WorkspacesWorkspaceIdPdpsPdpIdOriginalGet({
      path: { workspace_id, pdp_id },
      throwOnError: true,
    });
    return data;
  };
};

useGetOriginalPdpSnapshotQuery.schema = {
  method: 'get',
  path: '/v1/workspaces/{workspace_id}/pdps/{pdp_id}/original',
};
